import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { useCallback, useContext } from 'react';
import { useSelector } from 'react-redux';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { SessionContext } from '@wix/da-react-context/pkg/SessionContext';
import { useDeviationAttributes } from '../../DeviationViews/hooks/useDeviationAttributes';
import { getIsCurrentUserAuthorOf } from '../../redux/deviations/selectors';
import { useSubscriptionTier } from '../../utils/hooks/useSubscriptionTier';
import { ElementVisibility, WindowBreakpoint } from './types';
import { getAllowOffers, getHasBuyItNow, getHasOffers, getHasOwnOffer, getHasRejectedOffer, getIsAdoptableOfferType, getIsBeforeOfferEndTime, getIsPending } from '../../utils/adoptables';
import { ScreenSizeContext } from '../../Measured';
function useIsOwner(deviation) {
    const isOwnerSelector = useCallback((state)=>getIsCurrentUserAuthorOf(state, deviation), [
        deviation
    ]);
    return useSelector(isOwnerSelector);
}
// TODO: Expand with other common breakpoints
function useWindowBreakpoint() {
    const isMobile = useContext(MobileContext);
    const screenSize = useContext(ScreenSizeContext);
    if (isMobile) {
        return WindowBreakpoint.MobileAverageSize;
    }
    if (screenSize.width >= 1100) {
        return WindowBreakpoint.DesktopAverageSize;
    }
    return WindowBreakpoint.DesktopSmallSize;
}
function getEditBackgroundVisibility(determinationContext) {
    return determinationContext.deviationAttributes.isBackgroundEditable ? ElementVisibility.Bar : ElementVisibility.NotIncluded;
}
/**
 * Returns an object that maps ActionBar elements to their level of visibility.
 * (e.g. is this element on the bar? collapsed into the more menu? completely absent?)
 */ export function useElementVisibilityMap(deviation) {
    const windowBreakpoint = useWindowBreakpoint();
    const isOwner = useIsOwner(deviation);
    const deviationAttributes = useDeviationAttributes(deviation, isOwner);
    // These are collections of buttons. They can take up large amounts of space on the action bar,
    // so we use this object to determine if other more general controls should be removed/collapsed.
    const elementGroups = {
        adoptableBuyer: getAdoptableBuyerVisibilityGroup(deviation, isOwner),
        adoptableSeller: getAdoptableSellerVisibilityGroup(deviation, deviationAttributes, isOwner)
    };
    const determinationContext = {
        deviation,
        isOwner,
        deviationAttributes,
        windowBreakpoint,
        elementGroups
    };
    return {
        comment: getCommentVisibility(determinationContext),
        premiumDownload: getPremiumDownloadVisibility(determinationContext),
        freeDownload: getFreeDownloadVisibility(determinationContext),
        addToGroup: getAddToGroupVisibility(determinationContext),
        awardBadge: getAwardBadgeVisibility(determinationContext),
        favourite: getFavouriteVisibility(determinationContext),
        privateCollection: usePrivateCollectionVisibility(determinationContext),
        sellDeviation: getSellDeviationVisibility(determinationContext),
        subscribe: useSubscribeVisibility(determinationContext),
        editBackground: getEditBackgroundVisibility(determinationContext),
        deleteDeviation: getDeleteDeviationVisibility(determinationContext),
        editDeviation: getEditDeviationVisibility(determinationContext),
        linkNft: getLinkNftVisibility(determinationContext),
        share: getShareVisibility(determinationContext),
        report: getReportVisibility(determinationContext),
        fullscreen: useFullscreenVisibility(determinationContext),
        elementGroups: elementGroups
    };
}
function getDeleteDeviationVisibility(determinationContext) {
    return determinationContext.deviationAttributes.isDeletable ? ElementVisibility.Bar : ElementVisibility.NotIncluded;
}
function getEditDeviationVisibility(determinationContext) {
    return determinationContext.deviationAttributes.isEditable ? ElementVisibility.Bar : ElementVisibility.NotIncluded;
}
function getCommentVisibility(determinationContext) {
    const { deviationAttributes: { isCommentable }, windowBreakpoint, elementGroups } = determinationContext;
    if (!isCommentable) {
        return ElementVisibility.NotIncluded;
    }
    if (elementGroups.adoptableSeller) {
        return ElementVisibility.Menu;
    }
    if (elementGroups.adoptableBuyer && elementGroups.adoptableBuyer.countOnBar >= 2) {
        return ElementVisibility.Menu;
    }
    // And we'll collapse this control on smaller screens if either adoptable-related element groups are active.
    if ((elementGroups.adoptableBuyer || elementGroups.adoptableSeller) && windowBreakpoint === WindowBreakpoint.DesktopSmallSize) {
        return ElementVisibility.Menu;
    }
    return ElementVisibility.Bar;
}
function getFreeDownloadVisibility(determinationContext) {
    const { elementGroups, windowBreakpoint, deviationAttributes: { isDownloadable } } = determinationContext;
    if (!isDownloadable) {
        return ElementVisibility.NotIncluded;
    }
    if (elementGroups.adoptableBuyer && elementGroups.adoptableBuyer.countOnBar >= 2) {
        return ElementVisibility.Menu;
    }
    // And we'll collapse this control on smaller screens if either adoptable-related element groups are active.
    if ((elementGroups.adoptableBuyer || elementGroups.adoptableSeller) && windowBreakpoint === WindowBreakpoint.DesktopSmallSize) {
        return ElementVisibility.Menu;
    }
    return ElementVisibility.Bar;
}
function getPremiumDownloadVisibility(determinationContext) {
    const { elementGroups, windowBreakpoint, deviationAttributes: { isPurchasable } } = determinationContext;
    if (!isPurchasable) {
        return ElementVisibility.NotIncluded;
    }
    if (elementGroups.adoptableBuyer && elementGroups.adoptableBuyer.countOnBar >= 2) {
        return ElementVisibility.Menu;
    }
    // And we'll collapse this control on smaller screens if either adoptable-related element groups are active.
    if ((elementGroups.adoptableBuyer || elementGroups.adoptableSeller) && windowBreakpoint === WindowBreakpoint.DesktopSmallSize) {
        return ElementVisibility.Menu;
    }
    return ElementVisibility.Bar;
}
function getFavouriteVisibility(determinationContext) {
    return determinationContext.deviationAttributes.isFavouritable ? ElementVisibility.Bar : ElementVisibility.NotIncluded;
}
function getAddToGroupVisibility(determinationContext) {
    const { deviationAttributes: { isGroupSubmittable }, isOwner } = determinationContext;
    if (isGroupSubmittable) {
        return isOwner ? ElementVisibility.Bar : ElementVisibility.Menu;
    }
    return ElementVisibility.NotIncluded;
}
function getAwardBadgeVisibility(determinationContext) {
    const { elementGroups, windowBreakpoint, deviationAttributes: { isCoreBadgeAwardable } } = determinationContext;
    if (!isCoreBadgeAwardable) {
        return ElementVisibility.NotIncluded;
    }
    if (elementGroups.adoptableBuyer && elementGroups.adoptableBuyer.countOnBar >= 2) {
        return ElementVisibility.Menu;
    }
    // And we'll collapse this control on smaller screens if either adoptable-related element groups are active.
    if ((elementGroups.adoptableBuyer || elementGroups.adoptableSeller) && windowBreakpoint === WindowBreakpoint.DesktopSmallSize) {
        return ElementVisibility.Menu;
    }
    return ElementVisibility.Bar;
}
function usePrivateCollectionVisibility(determinationContext) {
    const isPrivateCollectionsFlagEnabled = useFlag('private_collections');
    const { isOwner, elementGroups, windowBreakpoint } = determinationContext;
    if (!isPrivateCollectionsFlagEnabled || isOwner) {
        return ElementVisibility.NotIncluded;
    }
    if (elementGroups.adoptableBuyer && elementGroups.adoptableBuyer.countOnBar >= 2) {
        return ElementVisibility.NotIncluded;
    }
    // And we'll collapse this control on smaller screens if either adoptable-related element groups are active.
    if ((elementGroups.adoptableBuyer || elementGroups.adoptableSeller) && windowBreakpoint === WindowBreakpoint.DesktopSmallSize) {
        return ElementVisibility.NotIncluded;
    }
    return ElementVisibility.Bar;
}
function useFullscreenVisibility(_determinationContext) {
    const { isLoggedIn } = useContext(SessionContext);
    return isLoggedIn ? ElementVisibility.Bar : ElementVisibility.NotIncluded;
}
function getSellDeviationVisibility(determinationContext) {
    var _deviation_adoptable, _deviation_extended;
    const { deviation } = determinationContext;
    var _deviation_adoptable_status;
    const adoptableStatus = (_deviation_adoptable_status = (_deviation_adoptable = deviation.adoptable) === null || _deviation_adoptable === void 0 ? void 0 : _deviation_adoptable.status) !== null && _deviation_adoptable_status !== void 0 ? _deviation_adoptable_status : 'not_onsale';
    return !!((_deviation_extended = deviation.extended) === null || _deviation_extended === void 0 ? void 0 : _deviation_extended.canUserSell) && adoptableStatus === 'not_onsale' ? ElementVisibility.Bar : ElementVisibility.NotIncluded;
}
function useSubscribeVisibility(determinationContext) {
    const tier = useSubscriptionTier(determinationContext.deviation);
    return !determinationContext.deviationAttributes.hasFlag && !!tier ? ElementVisibility.Bar : ElementVisibility.NotIncluded;
}
function getLinkNftVisibility(determinationContext) {
    const { isOwner, deviationAttributes: { hasNft } } = determinationContext;
    return isOwner && !hasNft ? ElementVisibility.Menu : ElementVisibility.NotIncluded;
}
function getShareVisibility(determinationContext) {
    return determinationContext.deviationAttributes.isShareable ? ElementVisibility.Menu : ElementVisibility.NotIncluded;
}
function getReportVisibility(determinationContext) {
    return determinationContext.deviationAttributes.isReportable ? ElementVisibility.Menu : ElementVisibility.NotIncluded;
}
function getAdoptableBuyerVisibilityGroup(deviation, isOwner) {
    var _deviation_extended_adoptable, _deviation_extended;
    if (!((_deviation_extended = deviation.extended) === null || _deviation_extended === void 0 ? void 0 : (_deviation_extended_adoptable = _deviation_extended.adoptable) === null || _deviation_extended_adoptable === void 0 ? void 0 : _deviation_extended_adoptable.canAdopt) || isOwner) {
        return undefined;
    }
    const allowOffers = getAllowOffers(deviation);
    const isBeforeOfferEndTime = getIsBeforeOfferEndTime(deviation);
    const hasOwnOffer = getHasOwnOffer(deviation);
    const canMakeNewOffer = allowOffers && isBeforeOfferEndTime && !hasOwnOffer;
    const hasRejectedOffer = getHasRejectedOffer(deviation);
    const canManageExistingOffer = allowOffers && isBeforeOfferEndTime && hasOwnOffer && !hasRejectedOffer;
    const hasBuyItNow = getHasBuyItNow(deviation);
    const isPending = getIsPending(deviation);
    const hasViewOffers = getHasOffers(deviation) && isPending;
    const elements = {
        buyItNow: hasBuyItNow ? ElementVisibility.Bar : ElementVisibility.NotIncluded,
        makeOffer: canMakeNewOffer ? ElementVisibility.Bar : ElementVisibility.NotIncluded,
        manageOffer: canManageExistingOffer ? ElementVisibility.Bar : ElementVisibility.NotIncluded,
        viewOffers: hasViewOffers ? ElementVisibility.Bar : ElementVisibility.NotIncluded
    };
    return _object_spread({
        countOnBar: Object.values(elements).filter((visibility)=>visibility === ElementVisibility.Bar).length
    }, elements);
}
function getAdoptableSellerVisibilityGroup(deviation, deviationAttributes, isOwner) {
    if (!deviationAttributes.isAdoptable || !isOwner) {
        return undefined;
    }
    const isOfferType = getIsAdoptableOfferType(deviation);
    const elements = {
        editSale: ElementVisibility.Bar,
        viewOffers: isOfferType ? ElementVisibility.Bar : ElementVisibility.NotIncluded
    };
    return _object_spread({
        countOnBar: Object.values(elements).filter((visibility)=>visibility === ElementVisibility.Bar).length
    }, elements);
}
